<template>
  <div
    class="plus-root relative flex h-screen min-h-0 flex-col overflow-hidden bg-[#0a0a0a] text-white antialiased md:flex-row"
  >
    <!-- 全屏背景图（胶片颗粒 + 顶部色光） -->
    <div
      class="plus-page-bg pointer-events-none fixed inset-0 z-0"
      :style="pageBgStyle"
      aria-hidden="true"
    ></div>
    <div class="plus-page-bg-tint pointer-events-none fixed inset-0 z-[1]" aria-hidden="true"></div>

    <!-- 顶栏极细渐变（左暖色 → 右冷色） -->
    <div
      class="plus-top-sheen pointer-events-none fixed left-0 right-0 top-0 z-[60] h-[3px] md:left-[4.5rem]"
      aria-hidden="true"
    ></div>

    <div class="relative z-[2] flex h-full min-h-0 w-full min-w-0 flex-1 flex-col md:flex-row">
      <PlusAppSidebar />

      <!-- 主区域 -->
    <div class="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <!-- 浅蓝促销条 -->
      <div
        v-if="promoVisible"
        class="relative z-40 flex shrink-0 items-center justify-center gap-3 border-b border-sky-300/30 bg-gradient-to-r from-sky-200 via-sky-100 to-sky-200 px-4 py-2.5 text-center text-slate-800"
      >
        <p class="max-w-3xl flex-1 text-[12px] leading-snug sm:text-[13px]">
          <strong class="font-semibold">Plus 1.0</strong>
          已上线：更稳的视频链路、更丰富模板，限时订阅
          <span class="whitespace-nowrap font-semibold text-sky-900">8 折</span>
          起。
        </p>
        <router-link
          to="/pricing"
          class="shrink-0 rounded-full bg-slate-900 px-4 py-1.5 text-[12px] font-semibold text-white no-underline shadow-md hover:bg-slate-800"
        >
          立即升级
        </router-link>
        <button
          type="button"
          class="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-600 hover:bg-black/10 sm:static sm:translate-y-0"
          aria-label="关闭"
          @click="promoVisible = false"
        >
          <i class="fas fa-times text-sm"></i>
        </button>
      </div>

      <div
        ref="mainScroll"
        class="plus-main-scroll min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden"
      >
        <div class="mx-auto w-full min-w-0 max-w-[1320px] px-4 pb-16 pt-8 sm:px-6 sm:pt-10 lg:px-10 lg:pt-12">
          <!-- Hero -->
          <div class="text-center">
            <h1
              class="text-balance text-[1.5rem] font-semibold leading-tight tracking-tight text-white sm:text-[1.85rem] lg:text-[2.1rem]"
            >
              i Doing - 你的专属AI视频创作Agent
            </h1>
            <p class="mx-auto mt-3 max-w-2xl text-[14px] leading-relaxed text-[#a0a0a0] sm:text-[15px]">
              <!-- 把灵感瞬间转化为视频，让想象力回归创意 -->
			  I'm doing, AI's doing
            </p>
          </div>

          <!-- 中央大输入（玻璃拟态 + 鼠标跟随流光边框） -->
          <div class="mx-auto mt-10 max-w-[920px]">
            <div
              class="plus-prompt-shell"
              :style="promptGlowStyle"
              @mousemove="onPromptPointerMove"
              @mouseleave="onPromptPointerLeave"
            >
              <div class="plus-prompt-panel">
                <PlusPromptComposer launch-target="canvas" />
              </div>
            </div>
          </div>

          <!-- 热门 Skills（图2：低调标题 + 圆形缩略 + 新文案） -->
          <div
            class="plus-skills-section relative mx-auto mt-14 w-full min-w-0 max-w-full overflow-visible rounded-2xl border border-white/[0.05] bg-[#0a0a0a] px-3 py-8 sm:px-6"
          >
            <p class="mb-5 text-center text-[12px] font-medium tracking-wide text-[#6b6b6b]">
              热门 Skills 热门技能
            </p>
            <div
              class="plus-skills-scroll min-w-0 w-full max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <div
                v-if="!userCenterReady"
                class="mx-auto flex w-max max-w-none flex-nowrap justify-center gap-3"
              >
                <div
                  v-for="i in 5"
                  :key="'skill-sk-' + i"
                  class="flex h-[68px] w-[260px] min-w-[260px] shrink-0 animate-pulse gap-2.5 rounded-[18px] border border-[#2c2c2c] bg-[#121212] px-3 py-2.5"
                >
                  <div class="h-10 w-10 shrink-0 rounded-full bg-white/[0.08]"></div>
                  <div class="min-w-0 flex-1 space-y-2 py-1">
                    <div class="h-3 w-24 rounded bg-white/[0.08]"></div>
                    <div class="h-2.5 w-32 rounded bg-white/[0.06]"></div>
                  </div>
                </div>
              </div>
              <div
                v-else-if="skillCards.length"
                class="plus-skills-row mx-auto flex w-max max-w-none flex-nowrap items-center justify-center gap-3"
              >
                <PlusSkillCard
                  v-for="card in skillCards"
                  :key="card.id"
                  :card="card"
                  @skill-video-show="onSkillVideoShow"
                  @skill-video-hide="onSkillVideoHide"
                />
              </div>
            </div>
          </div>

          <!-- 最近项目（仅登录用户） -->
          <PlusRecentProjects v-if="showRecentProjects" ref="recentProjects" />

          <!-- PlusTV -->
          <div class="relative z-[1] mx-auto mt-16 w-full max-w-full">
            <h2 class="text-lg font-bold tracking-tight text-white sm:text-xl">
              TV Show<!-- <span class="text-white/90">PlusTV</span> -->
            </h2>
            <div v-if="!squareListReady" class="plus-tv-mosaic mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <div
                v-for="i in 8"
                :key="'tv-sk-' + i"
                class="plus-tv-skeleton animate-pulse rounded-xl bg-white/[0.06]"
                :class="i === 0 ? 'col-span-2 aspect-video' : 'aspect-video'"
              ></div>
            </div>
            <p v-else-if="squareListError" class="mt-5 text-center text-[13px] text-rose-300/90">
              {{ squareListError }}
            </p>
            <p v-else-if="!squareList.length" class="mt-5 text-center text-[13px] text-white/45">
              暂无广场内容
            </p>
            <div v-else class="plus-tv-mosaic mt-5">
              <div
                v-if="showHeroRow"
                class="plus-tv-hero-row grid grid-cols-2 items-stretch gap-3 lg:grid-cols-4"
              >
                <PlusTvBannerCarousel
                  v-if="bannerList.length"
                  :slides="bannerList"
                  :hero-row-align="tvRow1Rest.length > 0"
                />
                <div
                  v-else-if="!bannerReady"
                  class="col-span-2 flex min-h-0 flex-col"
                >
                  <div class="aspect-video flex-1 animate-pulse rounded-xl bg-white/[0.06]"></div>
                  <div class="mt-2 h-4 w-2/5 animate-pulse rounded bg-white/[0.06]"></div>
                </div>
                <PlusTvSquareCard
                  v-for="item in tvRow1Rest"
                  :key="item.id"
                  :item="item"
                  variant="compact"
                />
              </div>
              <div
                v-if="tvGridRest.length"
                class="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4"
              >
                <PlusTvSquareCard
                  v-for="item in tvGridRest"
                  :key="item.id"
                  :item="item"
                  variant="compact"
                />
              </div>
            </div>
          </div>

          <footer class="mt-16 border-t border-white/[0.06] pt-8 text-center">
            <div class="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12px] text-[#a0a0a0]">
              <router-link to="/terms" class="no-underline transition hover:text-white">使用条款</router-link>
              <span class="text-white/15">|</span>
              <router-link to="/privacy" class="no-underline transition hover:text-white">隐私政策</router-link>
            </div>
          </footer>
        </div>
      </div>
    </div>
    </div>

    <!-- Skills 预览视频：挂到页面根层，避免被下方 PlusTV / 滚动区遮挡 -->
    <div
      v-show="skillVideo.visible && skillVideo.card && skillVideo.card.videoUrl"
      class="plus-skill-video-float"
      :style="skillVideoFloatStyle"
      @mouseenter="onSkillVideoFloatEnter"
      @mouseleave="onSkillVideoFloatLeave"
    >
      <div class="plus-skill-video-float__bridge" aria-hidden="true"></div>
      <video
        ref="skillPreviewVideo"
        class="plus-skill-video-float__media"
        :key="skillVideo.card ? skillVideo.card.id : 'skill-video'"
        :src="skillVideo.card ? skillVideo.card.videoUrl : ''"
        muted
        loop
        playsinline
        webkit-playsinline
        preload="auto"
        @loadeddata="onSkillPreviewLoaded"
      ></video>
    </div>
  </div>
</template>

<script>
import PlusAppSidebar from '../components/PlusAppSidebar.vue'
import PlusPromptComposer from '../components/PlusPromptComposer.vue'
import PlusRecentProjects from '../components/PlusRecentProjects.vue'
import PlusSkillCard from '../components/PlusSkillCard.vue'
import PlusTvBannerCarousel from '../components/PlusTvBannerCarousel.vue'
import PlusTvSquareCard from '../components/PlusTvSquareCard.vue'
import { fetchUserCenterConfig, fetchSkillsCatalog } from '../utils/bannerApi'
import { fetchSquareList } from '../utils/squareListApi'
import { isLoggedInUser, loginGate } from '../utils/loginGate'

export default {
  name: 'PlusLanding',
  components: {
    PlusAppSidebar,
    PlusPromptComposer,
    PlusRecentProjects,
    PlusSkillCard,
    PlusTvBannerCarousel,
    PlusTvSquareCard
  },
  data() {
    return {
      plusBgImageUrl:
        'https://plusyifanshang.oss-cn-hangzhou.aliyuncs.com/box/img/other/pGubwLzFhDAFgwTh2CWjnCeFC3Yo5tZrPrBOJJCE.webp',
      promoVisible: false,
      skillCards: [],
      userCenterReady: false,
      squareList: [],
      squareListReady: false,
      squareListError: '',
      squareCurrentPage: 1,
      squarePageTotal: 1,
      bannerList: [],
      bannerReady: false,
      skillVideo: {
        visible: false,
        card: null,
        rect: null
      },
      skillVideoHovering: false,
      skillVideoHideTimer: null,
      activeSkillCardId: null,
      skillVideoSession: 0,
      promptGlowX: 50,
      promptGlowY: 12,
      promptGlowActive: false,
      loginGate,
      authTick: 0
    }
  },
  watch: {
    'loginGate.visible'(visible, wasVisible) {
      if (wasVisible && !visible) {
        this.authTick += 1
        this.$nextTick(() => {
          const ref = this.$refs.recentProjects
          if (ref && typeof ref.loadProjects === 'function') ref.loadProjects()
        })
      }
    }
  },
  mounted() {
    this.authTick += 1
    this.loadSquareList()
    this.loadUserCenter()
  },
  beforeDestroy() {
    this.clearSkillVideoHideTimer()
    this.unbindSkillVideoScroll()
  },
  computed: {
    showRecentProjects() {
      void this.authTick
      return isLoggedInUser()
    },
    pageBgStyle() {
      const u = this.plusBgImageUrl
      if (!u) return {}
      return {
        backgroundImage: `url("${u}")`
      }
    },
    tvRow1Rest() {
      return this.squareList.slice(0, 2)
    },
    tvGridRest() {
      return this.squareList.slice(2)
    },
    showHeroRow() {
      return this.bannerList.length > 0 || this.tvRow1Rest.length > 0 || !this.bannerReady
    },
    promptGlowStyle() {
      return {
        '--prompt-mx': `${this.promptGlowX}%`,
        '--prompt-my': `${this.promptGlowY}%`,
        '--prompt-glow-opacity': this.promptGlowActive ? '1' : '0.45',
        '--prompt-bloom': this.promptGlowActive ? '1' : '0.4'
      }
    },
    skillVideoFloatStyle() {
      const r = this.skillVideo.rect
      if (!r) return { display: 'none' }
      const w = 260
      const h = 174
      const gap = 10
      const bridge = 14
      const left = r.left + Math.max(0, (r.width - w) / 2)
      return {
        position: 'fixed',
        top: `${r.bottom + gap}px`,
        left: `${left}px`,
        width: `${w}px`,
        height: `${h}px`,
        zIndex: 500,
        '--skill-video-gap': `${gap}px`,
        '--skill-video-bridge': `${bridge}px`
      }
    }
  },
  methods: {
    onPromptPointerMove(e) {
      const el = e.currentTarget
      if (!el || typeof el.getBoundingClientRect !== 'function') return
      const r = el.getBoundingClientRect()
      if (!r.width || !r.height) return
      this.promptGlowX = ((e.clientX - r.left) / r.width) * 100
      this.promptGlowY = ((e.clientY - r.top) / r.height) * 100
      this.promptGlowActive = true
    },
    onPromptPointerLeave() {
      this.promptGlowActive = false
      this.promptGlowX = 50
      this.promptGlowY = 12
    },
    async loadUserCenter() {
      this.bannerReady = false
      this.userCenterReady = false
      try {
        const [{ banners }, { skills }] = await Promise.all([
          fetchUserCenterConfig().catch(() => ({ banners: [], skills: [] })),
          fetchSkillsCatalog().catch(() => ({ skills: [] }))
        ])
        this.bannerList = Array.isArray(banners) ? banners : []
        this.skillCards = Array.isArray(skills) && skills.length ? skills : []
      } catch (e) {
        this.bannerList = []
        this.skillCards = []
        if (process.env.NODE_ENV === 'development') {
          console.warn('[user-center]', e)
        }
      } finally {
        this.bannerReady = true
        this.userCenterReady = true
      }
    },
    async loadSquareList() {
      this.squareListReady = false
      this.squareListError = ''
      try {
        const { list, currentPage, pageTotal } = await fetchSquareList({ page: 1, limit: 12 })
        this.squareList = Array.isArray(list) ? list : []
        this.squareCurrentPage = currentPage
        this.squarePageTotal = pageTotal
      } catch (e) {
        this.squareList = []
        this.squareListError = (e && e.message) || '广场列表加载失败，请稍后重试'
        if (process.env.NODE_ENV === 'development') {
          console.warn('[squarelist]', e)
        }
      } finally {
        this.squareListReady = true
      }
    },
    clearSkillVideoHideTimer() {
      if (this.skillVideoHideTimer) {
        clearTimeout(this.skillVideoHideTimer)
        this.skillVideoHideTimer = null
      }
    },
    onSkillVideoShow(payload) {
      const card = payload && payload.card
      const rect = payload && payload.rect
      const videoUrl = card && card.videoUrl ? String(card.videoUrl).trim() : ''
      if (!card || !videoUrl || !rect) return
      this.skillVideoSession += 1
      this.clearSkillVideoHideTimer()
      this.activeSkillCardId = card.id
      this.skillVideo.visible = true
      this.skillVideo.card = { ...card, videoUrl }
      this.skillVideo.rect = { ...rect }
      this.bindSkillVideoScroll()
      this.$nextTick(() => this.playSkillPreviewVideo())
    },
    onSkillVideoHide(cardId) {
      if (
        cardId != null &&
        this.activeSkillCardId != null &&
        String(cardId) !== String(this.activeSkillCardId)
      ) {
        return
      }
      this.scheduleSkillVideoHide()
    },
    scheduleSkillVideoHide() {
      this.clearSkillVideoHideTimer()
      const session = this.skillVideoSession
      this.skillVideoHideTimer = setTimeout(() => {
        if (session !== this.skillVideoSession) return
        if (this.skillVideoHovering) return
        this.hideSkillVideoNow()
      }, 320)
    },
    hideSkillVideoNow() {
      this.skillVideo.visible = false
      this.skillVideo.card = null
      this.skillVideo.rect = null
      this.activeSkillCardId = null
      this.stopSkillPreviewVideo()
      this.unbindSkillVideoScroll()
    },
    onSkillVideoFloatEnter() {
      this.skillVideoHovering = true
      this.clearSkillVideoHideTimer()
    },
    onSkillVideoFloatLeave() {
      this.skillVideoHovering = false
      this.scheduleSkillVideoHide()
    },
    updateSkillVideoRect() {
      if (!this.skillVideo.visible || !this.activeSkillCardId) return
      const id = String(this.activeSkillCardId).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      const el = this.$el.querySelector(`[data-skill-id="${id}"]`)
      if (!el) return
      const r = el.getBoundingClientRect()
      this.skillVideo.rect = {
        top: r.top,
        left: r.left,
        bottom: r.bottom,
        width: r.width
      }
    },
    bindSkillVideoScroll() {
      if (this._skillScrollBound) return
      this._onSkillScroll = () => this.updateSkillVideoRect()
      const el = this.$refs.mainScroll
      if (el) el.addEventListener('scroll', this._onSkillScroll, { passive: true })
      window.addEventListener('resize', this._onSkillScroll, { passive: true })
      this._skillScrollBound = true
    },
    unbindSkillVideoScroll() {
      if (!this._skillScrollBound) return
      const el = this.$refs.mainScroll
      if (el && this._onSkillScroll) el.removeEventListener('scroll', this._onSkillScroll)
      if (this._onSkillScroll) window.removeEventListener('resize', this._onSkillScroll)
      this._skillScrollBound = false
      this._onSkillScroll = null
    },
    onSkillPreviewLoaded() {
      if (this.skillVideo.visible) this.playSkillPreviewVideo()
    },
    playSkillPreviewVideo() {
      const v = this.$refs.skillPreviewVideo
      if (!v || !this.skillVideo.visible) return
      const tryPlay = () => {
        v.muted = true
        v.volume = 0
        const p = v.play()
        if (p && typeof p.catch === 'function') {
          p.catch(() => {})
        }
      }
      if (v.readyState >= 2) {
        tryPlay()
      } else {
        try {
          v.load()
        } catch (e) {
          /* ignore */
        }
        tryPlay()
      }
    },
    stopSkillPreviewVideo() {
      const v = this.$refs.skillPreviewVideo
      if (!v) return
      v.pause()
      try {
        v.currentTime = 0
      } catch (e) {
        /* ignore */
      }
    }
  }
}
</script>

<style scoped>
.plus-root {
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    'PingFang SC',
    'Microsoft YaHei',
    sans-serif;
}
.plus-page-bg {
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  background-color: #0a0a0a;
}
.plus-page-bg-tint {
  background:
    radial-gradient(ellipse 110% 50% at 50% 0%, rgba(8, 8, 10, 0.25) 0%, transparent 52%),
    linear-gradient(180deg, rgba(10, 10, 12, 0.15) 0%, rgba(10, 10, 12, 0.55) 45%, rgba(8, 8, 10, 0.88) 100%);
}
.plus-top-sheen {
  background: linear-gradient(90deg, #fdba8c 0%, #fecdd3 25%, #e0f2fe 55%, #f8fafc 100%);
  opacity: 0.85;
}
.plus-main-scroll {
  -webkit-overflow-scrolling: touch;
}
/* 中央输入：深海军蓝紫底 + 流光边框 */
.plus-prompt-shell {
  position: relative;
  border-radius: 24px;
  isolation: isolate;
  --plus-submit-bg: linear-gradient(180deg, #4a3a6e 0%, #2d2248 100%);
  --plus-submit-border: rgba(167, 139, 250, 0.22);
  --plus-submit-color: rgba(237, 233, 254, 0.96);
  --plus-submit-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  --plus-submit-shadow-hover: inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 0 16px rgba(109, 40, 217, 0.28);
}

/* 外侧光晕 bloom，跟随鼠标 */
.plus-prompt-shell::after {
  content: '';
  position: absolute;
  inset: -4px;
  z-index: 0;
  border-radius: 24px;
  pointer-events: none;
  opacity: var(--prompt-bloom, 0.4);
  transition: opacity 0.25s ease;
  background: radial-gradient(
    200px circle at var(--prompt-mx, 50%) var(--prompt-my, 50%),
    rgba(255, 255, 255, 0.75) 0%,
    rgba(191, 219, 254, 0.5) 22%,
    rgba(129, 140, 248, 0.28) 42%,
    rgba(167, 139, 250, 0.14) 58%,
    transparent 72%
  );
  filter: blur(18px);
}

/* 边框高亮流线，跟随鼠标 */
.plus-prompt-shell::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  padding: 1.5px;
  pointer-events: none;
  opacity: var(--prompt-glow-opacity, 0.45);
  transition: opacity 0.25s ease;
  background: radial-gradient(
    280px circle at var(--prompt-mx, 50%) var(--prompt-my, 50%),
    #ffffff 0%,
    rgba(255, 255, 255, 0.98) 6%,
    rgba(219, 234, 254, 0.82) 14%,
    rgba(165, 180, 252, 0.5) 24%,
    rgba(167, 139, 250, 0.32) 36%,
    transparent 54%
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  box-shadow:
    0 0 12px rgba(255, 255, 255, 0.35),
    0 0 28px rgba(147, 197, 253, 0.22),
    0 0 40px rgba(167, 139, 250, 0.12);
}

.plus-prompt-panel {
  position: relative;
  z-index: 1;
  overflow: hidden;
  border-radius: 23px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: linear-gradient(
    152deg,
    #1e1c28 0%,
    #181622 42%,
    #12121a 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    inset 0 -32px 56px rgba(99, 102, 241, 0.04),
    0 24px 80px -30px rgba(0, 0, 0, 0.82);
}

.plus-prompt-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  background:
    radial-gradient(ellipse 80% 60% at 100% 100%, rgba(99, 102, 241, 0.08) 0%, transparent 55%),
    radial-gradient(ellipse 50% 40% at 0% 0%, rgba(255, 255, 255, 0.03) 0%, transparent 50%);
}

/* 内侧反光，跟随鼠标 */
.plus-prompt-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  background: radial-gradient(
    320px ellipse at var(--prompt-mx, 50%) var(--prompt-my, 50%),
    rgba(255, 255, 255, 0.1) 0%,
    rgba(191, 219, 254, 0.04) 28%,
    rgba(167, 139, 250, 0.03) 40%,
    transparent 62%
  );
  opacity: var(--prompt-bloom, 0.4);
  transition: opacity 0.25s ease;
}

/* Skills 区外层高度固定，悬停视频不撑开布局 */
.plus-skills-section {
  overflow: visible;
}
.plus-skills-scroll {
  min-height: 68px;
}
.plus-skills-row {
  min-height: 68px;
}

.plus-skill-video-float {
  position: fixed;
  pointer-events: auto;
  overflow: visible;
}
/* 透明桥接：覆盖卡片底到视频顶之间的间隔，避免移入视频时误关闭 */
.plus-skill-video-float__bridge {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(-1 * (var(--skill-video-gap, 10px) + var(--skill-video-bridge, 14px)));
  height: calc(var(--skill-video-gap, 10px) + var(--skill-video-bridge, 14px));
  pointer-events: auto;
}
.plus-skill-video-float__media {
  display: block;
  height: 174px;
  width: 100%;
  object-fit: cover;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #0a0a0a;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.55);
}

/* 首行：右侧小卡总高度（封面+信息）决定行高，左侧大卡拉伸对齐 */
.plus-tv-hero-row {
  align-items: stretch;
}
</style>
